import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check, Sparkles, Eye, MessageCircle, Bot, Mail, Plus, Minus } from "lucide-react";

const CAL_LINK = "https://cal.com/kalza-studio-hdho6x";
const EMAIL = "hello@kalzastudio.com";

/* Wine highlight */
const Highlight = ({ children }) => (
  <span className="relative inline-block font-accent">
    <span className="absolute left-0 right-0 bottom-[0.08em] h-[0.8em] bg-[#BE123C] -z-0" />
    <span className="relative z-10">{children}</span>
  </span>
);

/* Organic image */
const OrganicImage = ({ src, alt, className = "" }) => (
  <div
    className={`aspect-[4/5] overflow-hidden max-w-[65%] lg:max-w-none ${className}`}
    style={{
      WebkitMaskImage: 'url(/images/mood/shape-mask.png)',
      maskImage: 'url(/images/mood/shape-mask.png)',
      WebkitMaskSize: '100% 100%',
      maskSize: '100% 100%',
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
    }}
  >
    <img src={src} alt={alt || ""} className="w-full h-full object-cover" />
  </div>
);

/* Products */
const PRODUCTS = [
  {
    id: "make-me-memorable",
    Icon: Sparkles,
    eyebrow: "CONTENT AUDIT",
    name: "Make Me Memorable",
    tagline: "Your content stops blending in",
    price: "€29",
    pricePLN: "129 PLN",
    bestFor: "Founders whose posts, bios and captions feel generic and forgettable",
    description:
      "An AI agent that reads your brand content and tells you exactly where it sounds like everyone else in your niche. Scores five layers: atmosphere, visual vibe, hooks, messaging, edge. Then rewrites the weakest parts in a voice that actually sticks.",
    includes: [
      "Scores your content across five memorability layers",
      "Rewrites the hooks and bios that are not landing",
      "Tells you why something feels off, not just that it does",
      "Paste anything, get feedback in real time",
    ],
    checkoutUrl: "#",
    learnMoreUrl: "#",
  },
  {
    id: "stories-that-sell",
    Icon: MessageCircle,
    eyebrow: "INSTAGRAM STORIES",
    name: "Stories That Sell",
    tagline: "Sequences that actually convert",
    price: "€49",
    pricePLN: "199 PLN",
    bestFor: "Founders selling services or digital products through Instagram Stories",
    description:
      "69 proven Stories sequences for every stage of a sales campaign. Trust-building, objection-handling, launch-day, quiet-close. The agent adapts each sequence to your offer, your audience and your tone, so nothing sounds like a template.",
    includes: [
      "69 ready sequences from warm-up to closing the sale",
      "Scripts you can film, type out or turn into carousels",
      "Adapts every output to your offer and your voice",
      "Works in any language",
    ],
    checkoutUrl: "#",
    learnMoreUrl: "#",
  },
  {
    id: "brand-blind-spots",
    Icon: Eye,
    eyebrow: "IDENTITY DIAGNOSTIC",
    name: "Brand Blind Spots",
    tagline: "See what you cannot see yourself",
    price: "€69",
    pricePLN: "299 PLN",
    bestFor: "Founders who want to build something of their own but lack confidence and have fears about the areas that will overwhelm them",
    description:
      "A guided diagnostic that names the gap between who you actually are and how your brand currently shows up. Works through identity blocks, people-pleasing patterns and perfectionism until you can see what your brand is hiding. Produces a manifest, content pillars and a clear direction.",
    includes: [
      "Asks diagnostic questions before jumping to answers",
      "Names blind spots and identity blocks you cannot see on your own",
      "Builds a brand manifest in your words",
      "Gives you a concrete plan you can start tomorrow",
    ],
    checkoutUrl: "#",
    learnMoreUrl: "#",
  },
  {
    id: "dna-voice",
    Icon: Bot,
    eyebrow: "PERSONAL AI AGENT",
    name: "DNA Voice",
    tagline: "AI that writes like you, not like ChatGPT",
    price: "€97",
    pricePLN: "399 PLN",
    bestFor: "Founders tired of editing generic AI output and rewriting every draft in their own voice",
    description:
      "A personal AI agent trained on your writing voice through a ten-layer analysis. Asks diagnostic questions, captures your specific rhythm and reflexes and becomes the one AI tool that actually sounds like you on a good day. Use it for captions, emails, sales pages and newsletters.",
    includes: [
      "Completely customized to your writing voice",
      "Extracts your voice through ten diagnostic layers",
      "Learns your rhythm, your reflexes, your edge",
      "Everything it writes sounds like you on a good day",
      "Works in Claude and ChatGPT",
    ],
    checkoutUrl: "#",
    learnMoreUrl: "#",
  },
];

/* Real user reactions — unedited, WhatsApp-style */
const REACTIONS = [
  {
    text: "wow! it really tells u stuff 👀 i love it!",
    product: "Brand Blind Spots",
  },
  {
    text: "f*cking hell 😂 i love that. It gave me some clarity",
    product: "Brand Blind Spots",
  },
  {
    text: "Ten agent to jest game changer 😍",
    product: "DNA Voice",
  },
  {
    text: "Wrote 5 Instagram captions in 20 minutes and for the first time they actually sounded like me, not like a chatbot wearing my name",
    product: "DNA Voice",
  },
  {
    text: "Hmmm, Ola, ten agent to jest coś pięknego. Chat GPT to przy tym kretyn 🙏",
    product: "DNA Voice + Make Me Memorable",
  },
  {
    text: "I kept putting off my rebrand for months because I could not figure out what was off. This thing told me in 40 minutes",
    product: "Make Me Memorable",
  },
  {
    text: "To jest doje*ane. Trafia w punkt. Wyłapał że nie mam konsekwencji w zarządzaniu zespołem, że kieruję się sympatią a nie konsekwencją. Spięło mnie to w kontekście tego jak chcę prowadzić restaurację. Rewolucyjne narzędzie jak ktoś myśli o założeniu swojej marki.",
    product: "Brand Blind Spots",
  },
  {
    text: "Pomógł mi znaleźć punkt w którym utknąłem który nie był dla mnie oczywisty",
    product: "Brand Blind Spots",
  },
  {
    text: "Fajne narzędzie które pomoże ci rozwiać wątpliwości i faktycznie nakierować na to co jest ważne",
    product: "Brand Blind Spots",
  },
  {
    text: "I used my mom's clothing store as a case study and the tool managed to identify the core issue really well. The language was super accessible and it built very structured hypotheses, I really thought that was impressive! I have no idea how you even build something at this level 😍",
    product: "Brand Blind Spots",
  },
];

const FAQ_ITEMS = [
  {
    q: "Do I need Claude or ChatGPT Plus to use these?",
    a: "Yes. Each agent runs inside your own Claude Project or Custom GPT. You need an active subscription to one of those platforms. The agent files work on free Claude accounts too, just with the usual free-tier limits.",
  },
  {
    q: "Is this a subscription?",
    a: "No. Each product is a one-time purchase. You buy the agent, you own it, you use it for as long as you want.",
  },
  {
    q: "How is this different from just using ChatGPT?",
    a: "I have been working on ChatGPT and Claude for around four years. I know both tools inside out. That matters because these agents are not just prompts with a nice name on top. They carry specific brand methodology that I refined over dozens of projects. Generic AI gives you generic output. These agents give you the same thinking I bring to my 1:1 clients.",
  },
  {
    q: "Can I use these for my clients?",
    a: "Yes, for your own work with clients. You cannot resell or redistribute the agent files themselves.",
  },
  {
    q: "What if I want something more custom?",
    a: "In closer work with my 1:1 clients, I also build custom agents designed around their specific business, voice and goals. Fully doable. Book a call and we can talk about what you actually need.",
  },
];

function FaqItem({ item, open, onToggle }) {
  return (
    <div className="border-t border-[#1C1210]/10">
      <button
        onClick={onToggle}
        className="w-full flex items-start justify-between gap-6 py-6 text-left group"
      >
        <h3 className="font-serif text-lg text-[#1C1210] group-hover:text-[#BE123C] transition-colors duration-300">{item.q}</h3>
        <span className="flex-shrink-0 text-[#BE123C] mt-1">
          {open ? <Minus size={20} /> : <Plus size={20} />}
        </span>
      </button>
      {open && (
        <div className="pb-6">
          <p className="font-body text-sm text-[#6B6560] leading-[1.7]">{item.a}</p>
        </div>
      )}
    </div>
  );
}

export default function AiProductsPage() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <main data-testid="ai-products-page">
      {/* ═══ 1. HERO (dark, short, left-aligned) ═══ */}
      <section className="relative pt-28 pb-16 md:pt-36 md:pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-3xl">
            <span className="font-eyebrow text-white mb-6 block">AI PRODUCTS BY KALZA STUDIO</span>
            <h1 className="mb-6">
              <span className="font-display text-4xl md:text-5xl lg:text-6xl text-white block leading-[0.95]">MY METHODOLOGY</span>
              <span className="font-display text-4xl md:text-5xl lg:text-6xl text-white block leading-[0.95]">IN AN AGENT</span>
              <span className="font-accent text-4xl md:text-5xl lg:text-6xl text-[#BE123C] block leading-[1.1] mt-1">you can use today</span>
            </h1>
            <p className="font-body text-base md:text-lg text-white/70 leading-relaxed max-w-2xl">
              Four AI agents built on the same brand thinking I use with 1:1 clients. Works in Claude or ChatGPT. Yours forever.
            </p>
          </motion.div>
        </div>
      </section>


      {/* ═══ 2. PRODUCTS GRID (Apple grey) — moved up, right after hero ═══ */}
      <section className="py-20 md:py-28 bg-[#f5f5f7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-16 max-w-2xl">
            <span className="font-eyebrow text-[#8B1A3A] mb-4 block">THE AGENTS</span>
            <h2>
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">FOUR WAYS TO MAKE</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">your brand land harder</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {PRODUCTS.map((product, i) => (
              <motion.div
                key={product.id}
                id={product.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="bg-white border border-[#1C1210]/8 p-8 md:p-10 flex flex-col"
              >
                <div className="mb-6">
                  <product.Icon size={28} className="text-[#BE123C] mb-5" strokeWidth={1.5} />
                  <span className="inline-block font-eyebrow text-[10px] tracking-[2px] text-[#8B1A3A] mb-3">AI AGENT · {product.eyebrow}</span>
                  <h3 className="font-display text-2xl md:text-3xl text-[#1C1210] leading-[1] mb-2">{product.name}</h3>
                  <p className="font-accent text-lg text-[#BE123C] leading-[1.2]">{product.tagline}</p>
                </div>

                <p className="font-body text-sm text-[#6B6560] leading-[1.7] mb-5">{product.description}</p>

                <div className="mb-6">
                  <p className="font-eyebrow text-[#1C1210] mb-3">WHAT YOU GET</p>
                  <ul className="space-y-2">
                    {product.includes.map((item, j) => (
                      <li key={j} className="flex items-start gap-2">
                        <Check size={14} className="text-[#BE123C] flex-shrink-0 mt-1" />
                        <span className="font-body text-sm text-[#4A4540] leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mb-5 pb-5 border-b border-[#1C1210]/8">
                  <p className="font-eyebrow text-[#6B6560]/70 mb-2">BEST FOR</p>
                  <p className="font-body text-sm text-[#4A4540] italic leading-relaxed">{product.bestFor}</p>
                </div>

                <div className="mt-auto">
                  <div className="flex items-baseline gap-3 mb-5">
                    <span className="font-display text-3xl text-[#1C1210]">{product.price}</span>
                    <span className="font-body text-sm text-[#6B6560]">/ {product.pricePLN}</span>
                    <span className="font-body text-xs text-[#6B6560]/70 ml-auto">one-time</span>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a
                      href={product.checkoutUrl}
                      className="flex-1 inline-flex items-center justify-center gap-2 bg-[#BE123C] text-white font-body font-medium text-xs tracking-[2px] uppercase px-6 py-4 hover:bg-[#9F1239] transition-colors duration-300"
                    >
                      BUY NOW <ArrowUpRight size={14} />
                    </a>
                    <Link
                      to={product.learnMoreUrl}
                      className="inline-flex items-center justify-center gap-2 border border-[#1C1210]/15 text-[#1C1210] font-body font-medium text-xs tracking-[2px] uppercase px-6 py-4 hover:border-[#BE123C] hover:text-[#BE123C] transition-colors duration-300"
                    >
                      LEARN MORE <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 3. REAL REACTIONS (white, WhatsApp-style) — social proof before offer ═══ */}
      <section className="py-20 md:py-28 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-12 max-w-3xl">
            <span className="font-eyebrow text-[#8B1A3A] mb-4 block">EARLY REACTIONS</span>
            <h2 className="mb-6">
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">FIRST FOUNDERS</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">who tested these</span>
            </h2>
            <p className="font-body text-sm text-[#6B6560] leading-relaxed max-w-xl">
              Unfiltered messages from early-access founders. Swearing and emojis included. Proper business outcome stories coming as these agents mature 😉
            </p>
          </motion.div>
        </div>

        <div className="relative overflow-hidden">
          <div className="flex animate-reactions-scroll gap-5 py-2">
            {[...REACTIONS, ...REACTIONS].map((r, i) => (
              <div
                key={i}
                className="flex-shrink-0 w-[300px] md:w-[360px] bg-[#f5f5f7] px-5 py-4 rounded-2xl rounded-tl-sm border border-[#E8E3DE]/60"
              >
                <p className="font-body text-[15px] text-[#1C1210] leading-[1.55]">{r.text}</p>
                <div className="mt-3 pt-3 border-t border-[#1C1210]/8">
                  <span className="font-eyebrow text-[9px] tracking-[1.5px] text-[#8B1A3A]">{r.product}</span>
                </div>
              </div>
            ))}
          </div>
          <style>{`
            @keyframes reactionsScroll {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            .animate-reactions-scroll {
              animation: reactionsScroll 80s linear infinite;
              width: max-content;
            }
            .animate-reactions-scroll:hover {
              animation-play-state: paused;
            }
          `}</style>
        </div>
      </section>

      {/* ═══ 4. WHY I BUILT THESE (Apple grey) — after social proof ═══ */}
      <section className="py-20 md:py-28 bg-[#f5f5f7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <OrganicImage src="/images/ola-ai-products.jpg" alt="Aleksandra Kałuża" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-4 block">WHY I BUILT THESE</span>
              <h2 className="mb-6">
                <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">I LIKE BEING AHEAD</span>
                <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">of what is coming</span>
              </h2>
              <div className="space-y-4 font-body text-base text-[#6B6560] leading-[1.7]">
                <p>
                  I studied multimedia, but a big part of that education was actually technology: working with automations, creative projects and the side of the business most people call IT. I work in a creative field, but today the creative field runs on tech.
                </p>
                <p>
                  I love technology. I love learning new tools. I hate repetitive, boring tasks. If something can be simplified, systemised or automated, I will build the system. I have been using AI in real client work for three years now. For me this is not new.
                </p>
                <p>
                  At this point I know these tools well enough to make them actually useful. My clients are happy with what these agents produce. This is a small, polished menu. I am not planning to release new ones just to have more products. These are the ones that earned their place.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ 5. HOW IT WORKS (white) ═══ */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-16 max-w-2xl">
            <span className="font-eyebrow text-[#8B1A3A] mb-4 block">HOW IT WORKS</span>
            <h2>
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">BUY IT, PLUG IT IN,</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">start using it today</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 mb-12">
            {[
              { step: "01", title: "Pick your agent", body: "Choose the product that matches what your brand needs most right now. Buy it once." },
              { step: "02", title: "Get the files", body: "You receive the agent instructions as a Claude skill or Custom GPT configuration, plus a quick setup guide." },
              { step: "03", title: "Use it in your AI", body: "Drop it into your own Claude Project or ChatGPT workspace. The agent is ready to go the moment you open it." },
            ].map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="border-t border-[#1C1210]/15 pt-6"
              >
                <span className="font-display text-4xl text-[#BE123C] block mb-4 leading-none">{s.step}</span>
                <h3 className="font-serif text-xl text-[#1C1210] mb-3">{s.title}</h3>
                <p className="font-body text-sm text-[#6B6560] leading-[1.7]">{s.body}</p>
              </motion.div>
            ))}
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.3 }}>
            <a href="#make-me-memorable" className="inline-flex items-center justify-center gap-2 bg-[#BE123C] text-white font-body font-medium text-xs tracking-[2px] uppercase px-8 py-4 hover:bg-[#9F1239] transition-colors duration-300">
              PICK YOUR AGENT <ArrowRight size={14} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* ═══ 6. DIVIDER / QUALITY COMMITMENT (dark, centered, smaller) ═══ */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/70" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <p className="font-serif text-xl md:text-2xl text-white leading-[1.5]">
              I kept refining these agents obsessively until the output was sharp, consistent and something I was proud of. Because I use them with my own clients and I care about <Highlight>quality</Highlight> more than anything.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══ 7. FAQ (accordion) ═══ */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="font-eyebrow text-[#8B1A3A] mb-6 block">COMMON QUESTIONS</span>
            <h2 className="mb-10">
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">BEFORE YOU BUY</span>
            </h2>
            <div>
              {FAQ_ITEMS.map((item, i) => (
                <FaqItem
                  key={i}
                  item={item}
                  open={openFaq === i}
                  onToggle={() => setOpenFaq(openFaq === i ? null : i)}
                />
              ))}
              <div className="border-t border-[#1C1210]/10" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ 8. FINAL CTA (dark, left, email only) ═══ */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="font-eyebrow text-white mb-6 block">NEED HELP?</span>
            <h2 className="mb-8">
              <span className="font-display text-3xl md:text-5xl text-white block leading-[0.95]">NOT SURE</span>
              <span className="font-accent text-3xl md:text-5xl text-[#BE123C] block leading-[1.1] mt-2">which one?</span>
            </h2>
            <p className="font-body text-base md:text-lg text-white/70 leading-relaxed mb-10 max-w-xl">
              Send me a short message. I will tell you which agent matches what you actually need. Or if what you need is an Audit or Brand Foundation instead, I will say that too.
            </p>
            <a href={`mailto:${EMAIL}`} className="inline-flex items-center justify-center gap-2 bg-[#E11D48] text-white font-body font-medium text-xs tracking-[2px] uppercase px-10 py-5 hover:bg-[#BE123C] transition-colors duration-300">
              <Mail size={14} /> {EMAIL}
            </a>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
